import { useState, useEffect, useCallback } from "react";
import { Upload, FileText, File, Trash2, Wand2, Loader2, Search, AlertTriangle, Scale, Clock, Download, Pencil } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import CommandLayout from "@/components/CommandLayout";
import IntelligencePanel from "@/components/IntelligencePanel";
import AuthModal from "@/components/AuthModal";
import { Badge } from "@/components/ui/badge";
import { useShieldCredits } from "@/hooks/useShieldCredits";
import RefillModal from "@/components/RefillModal";
import PaywallModal from "@/components/PaywallModal";
import FeatureInterestButton from "@/components/FeatureInterestButton";
import DocumentViewerModal from "@/components/DocumentViewerModal";

interface VaultDocument {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  smart_badges: string[];
  ai_summary: string | null;
  toxicity_score: number | null;
  created_at: string;
}

const ALLOWED_MIME_TYPES: Record<string, string[]> = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "text/plain": [".txt"],
};

const isFileAllowed = (file: File): boolean => {
  const mime = file.type;
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
  const allowedExts = ALLOWED_MIME_TYPES[mime];
  return !!allowedExts && allowedExts.includes(ext);
};

const sanitizeAndRename = (fileName: string): string => {
  const ext = fileName.toLowerCase().slice(fileName.lastIndexOf("."));
  const allowedExtensions = Object.values(ALLOWED_MIME_TYPES).flat();
  const safeExt = allowedExtensions.includes(ext) ? ext : "";
  return `${crypto.randomUUID()}${safeExt}`;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getBadgeIcon = (badge: string) => {
  const lower = badge.toLowerCase();
  if (lower.includes("toxic") || lower.includes("high")) return AlertTriangle;
  if (lower.includes("legal")) return Scale;
  if (lower.includes("urgent")) return Clock;
  return FileText;
};

const getBadgeVariant = (badge: string): "default" | "destructive" | "secondary" | "outline" => {
  const lower = badge.toLowerCase();
  if (lower.includes("toxic") || lower.includes("high")) return "destructive";
  if (lower.includes("urgent")) return "default";
  return "secondary";
};

const Vault = () => {
  const { user } = useAuth();
  const { credits, isPaidUser, refetch: refetchCredits, deductCredit } = useShieldCredits();
  const [documents, setDocuments] = useState<VaultDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<VaultDocument | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showRefillModal, setShowRefillModal] = useState(false);
  const [viewerDoc, setViewerDoc] = useState<VaultDocument | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    // RLS enforced: only authenticated users can access their own row
    const { data, error } = await supabase
      .from("vault_documents")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching documents:", error);
      toast.error("Failed to load documents");
    } else {
      setDocuments(data || []);
    }
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // @security-enforcement: Server-side validation handled by Supabase Storage RLS policy.
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !user) return;

    const file = files[0];

    // GATE 1 (Frontend): Validate file.type and file.name extension
    // Security: Storage RLS policy enforces file extension constraints at the database level.
    // @security-audit: File type enforced by Storage RLS 'Strict File Type Enforcement' policy.
    if (!isFileAllowed(file)) {
      toast.error("Only PDF, JPG, PNG, and TXT files are allowed. File extension must match its type.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setIsUploading(true);
    
    try {
      // Upload to storage with sanitized UUID filename and explicit contentType
      const safeFileName = sanitizeAndRename(file.name);
      const filePath = `${user.id}/${safeFileName}`;
      const { error: uploadError } = await supabase.storage
        .from("vault-files")
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) {
        const msg = uploadError.message?.toLowerCase() || '';
        if (msg.includes('security') || msg.includes('policy') || msg.includes('violates') || msg.includes('not allowed') || (uploadError as any).status === 403) {
          toast.error("File type rejected by server. Only PDF, JPG, PNG, and TXT files are permitted.");
          return;
        }
        throw uploadError;
      }

      // Create document record
      const { data: docData, error: docError } = await supabase
        .from("vault_documents")
        .insert({
          user_id: user.id,
          file_name: file.name,
          file_path: filePath,
          file_type: file.type,
          file_size: file.size,
          smart_badges: [],
        })
        .select()
        .single();

      if (docError) throw docError;

      // Trigger AI analysis in background
      analyzeDocument(docData.id, filePath);

      toast.success("Document uploaded! AI analysis starting...");
      fetchDocuments();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload document");
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  };

  const analyzeDocument = async (documentId: string, filePath: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('analyze-document', {
        body: { documentId, filePath },
      });

      if (error) {
        console.error("Analysis failed:", error);
        const errorMsg = error.message || "";
        if (errorMsg.includes('credits_depleted') || errorMsg.includes('402')) {
          setShowRefillModal(true);
        } else if (errorMsg.includes('429')) {
          toast.error("Rate limit exceeded. Please wait a moment and try again.");
        } else {
          toast.error(error.message || "Analysis failed. Please try again.");
        }
        return;
      }

      fetchDocuments();
      refetchCredits();
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Analysis failed. Please try again.");
    }
  };

  const handleDownload = async (doc: VaultDocument, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { data, error } = await supabase.storage
        .from("vault-files")
        .download(doc.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Download started");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    }
  };

  const handleDelete = async (doc: VaultDocument) => {
    if (!confirm(`Delete "${doc.file_name}"?`)) return;

    try {
      // Delete from storage
      await supabase.storage.from("vault-files").remove([doc.file_path]);

      // Delete record
      const { error } = await supabase
        .from("vault_documents")
        .delete()
        .eq("id", doc.id);

      if (error) throw error;

      toast.success("Document deleted");
      if (selectedDocument?.id === doc.id) {
        setSelectedDocument(null);
      }
      fetchDocuments();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete document");
    }
  };

  // Collect all unique badges for filter bar
  const allBadges = Array.from(new Set(documents.flatMap(doc => doc.smart_badges || [])));

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.file_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTag = !activeTagFilter || (doc.smart_badges && doc.smart_badges.includes(activeTagFilter));
    return matchesSearch && matchesTag;
  });

  if (!user) {
    return (
      <CommandLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
          <div className="glass-card p-8 text-center max-w-md">
            <FileText className="w-16 h-16 mx-auto mb-4 text-cyan opacity-60" />
            <h2 className="text-2xl font-bold mb-2">The Vault</h2>
            <p className="text-muted-foreground mb-6">
              Sign in to access your secure document vault with AI-powered analysis.
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className={cn(
                "px-6 py-3 rounded-xl font-semibold",
                "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                "hover:scale-[1.02] transition-transform"
              )}
            >
              Sign In
            </button>
          </div>
        </div>
        <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
      </CommandLayout>
    );
  }

  return (
    <CommandLayout>
      <div className="flex gap-6 h-[calc(100vh-120px)]">
        {/* Documents List */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-1">
                <span className="text-foreground">The </span>
                <span className="text-gradient-cyan">Vault</span>
              </h1>
              <p className="text-muted-foreground">
                Your secure document storage with AI intelligence
              </p>
            </div>

            <label
              className={cn(
                "flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold cursor-pointer",
                "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                "hover:scale-[1.02] transition-transform",
                "shadow-[0_0_20px_rgba(0,242,255,0.3)]",
                isUploading && "opacity-50 pointer-events-none"
              )}
            >
              {isUploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Upload className="w-5 h-5" />
              )}
              {isUploading ? "Uploading..." : "Upload Document"}
              <input
                type="file"
              accept=".pdf,.jpg,.jpeg,.png,.txt"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isUploading}
              />
            </label>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={cn(
                "w-full pl-10 pr-4 py-3 rounded-xl",
                "bg-charcoal-medium border border-glass-border",
                "text-foreground placeholder:text-muted-foreground",
                "focus:outline-none focus:ring-2 focus:ring-cyan/50"
              )}
            />
          </div>

          {/* Tag Filter Bar */}
          {allBadges.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {allBadges.map((badge) => {
                const isActive = activeTagFilter === badge;
                const Icon = getBadgeIcon(badge);
                return (
                  <button
                    key={badge}
                    onClick={() => setActiveTagFilter(isActive ? null : badge)}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border cursor-pointer",
                      isActive
                        ? "bg-cyan text-primary-foreground border-cyan shadow-[0_0_12px_rgba(0,242,255,0.4)]"
                        : "bg-charcoal-medium text-muted-foreground border-glass-border hover:border-cyan/40 hover:text-foreground"
                    )}
                  >
                    <Icon className="w-3 h-3" />
                    {badge}
                  </button>
                );
              })}
            </div>
          )}

          {/* Documents List */}
          <div className="flex-1 overflow-y-auto space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-8 h-8 text-cyan animate-spin" />
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <div className="relative inline-block mb-6">
                  <Upload className="w-16 h-16 text-muted-foreground" />
                  <div className="absolute inset-0 blur-2xl bg-cyan/20 -z-10" />
                </div>
                <h3 className="text-xl font-semibold mb-2">
                  {searchQuery ? "No documents found" : "Your Vault is Empty"}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery
                    ? "Try a different search term"
                    : "Upload your first document to unlock AI insights"}
                </p>
                {!searchQuery && (
                  <label
                    className={cn(
                      "inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold cursor-pointer",
                      "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                      "hover:scale-[1.02] transition-transform",
                      "shadow-[0_0_30px_rgba(0,242,255,0.4)] animate-pulse-glow"
                    )}
                  >
                    <Upload className="w-5 h-5" />
                    Upload Document
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.txt"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            ) : (
              filteredDocuments.map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => setSelectedDocument(doc)}
                  className={cn(
                    "glass-card p-4 cursor-pointer transition-all duration-200 group",
                    "hover:border-cyan/30",
                    selectedDocument?.id === doc.id && "border-cyan/50 bg-cyan/5"
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-charcoal-light shrink-0">
                      <File className="w-6 h-6 text-cyan" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate group-hover:text-cyan transition-colors">
                        {doc.file_name}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(doc.file_size)} • {new Date(doc.created_at).toLocaleDateString()}
                      </p>
                      
                      {/* Smart Badges */}
                      {doc.smart_badges && doc.smart_badges.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {doc.smart_badges.map((badge, i) => {
                            const Icon = getBadgeIcon(badge);
                            return (
                              <Badge 
                                key={i} 
                                variant={getBadgeVariant(badge)} 
                                className="gap-1 text-xs shrink-0"
                              >
                                <Icon className="w-3 h-3" />
                                {badge}
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewerDoc(doc);
                        }}
                        className="p-2 rounded-lg hover:bg-charcoal-light text-muted-foreground hover:text-cyan"
                        title="View Document"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDocument(doc);
                        }}
                        className="p-2 rounded-lg hover:bg-charcoal-light text-muted-foreground hover:text-cyan"
                        title="Analyze Document"
                      >
                        <Wand2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleDownload(doc, e)}
                        className="p-2 rounded-lg hover:bg-charcoal-light text-muted-foreground hover:text-cyan"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(doc);
                        }}
                        className="p-2 rounded-lg hover:bg-destructive/20 text-muted-foreground hover:text-destructive"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Intelligence Panel with click-outside backdrop */}
        {selectedDocument && (
          <>
            {/* Backdrop for click-outside close */}
            <div 
              className="fixed inset-0 z-40 bg-black/20 lg:hidden" 
              onClick={() => setSelectedDocument(null)}
            />
            <div className="relative z-50">
              <IntelligencePanel
                vaultDoc={selectedDocument}
                onClose={() => setSelectedDocument(null)}
                onCreditUsed={refetchCredits}
              />
            </div>
          </>
        )}
      </div>


      <DocumentViewerModal
        document={viewerDoc}
        open={!!viewerDoc}
        onOpenChange={(open) => { if (!open) setViewerDoc(null); }}
        onNeutralize={(doc) => setSelectedDocument(doc)}
      />

      {isPaidUser ? (
        <RefillModal 
          open={showRefillModal} 
          onOpenChange={setShowRefillModal} 
          currentCredits={credits}
        />
      ) : (
        <PaywallModal open={showRefillModal} onOpenChange={setShowRefillModal} />
      )}
    </CommandLayout>
  );
};

export default Vault;
